import { buttonVariants } from "@/components/ui/button";
import { PRESENTATION_UPLOAD_HINT } from "@/lib/presentation-source";
import { cn } from "@/lib/utils";
import { Check, CheckCircle, File as FileIcon, Trash2, UploadCloud } from "lucide-react";
import type { ReactNode } from "react";
import { PRE_DECK_FILE, PRE_GENERATION_PROGRESS } from "./pre-fixture";

export const PRESENTATION_PHASES = ["Reading your slides", "Writing summary", "Rendering slides"];

export const HANDYOUT_PHASES = ["Preparing", "Choosing modules per slide", "Finalizing handyout"];

export function StaticButton({
  variant = "default",
  size = "sm",
  className,
  children,
}: {
  variant?: "default" | "ghost" | "outline";
  size?: "xs" | "sm" | "icon-sm";
  className?: string;
  children: ReactNode;
}) {
  return (
    <span aria-hidden className={cn(buttonVariants({ variant, size }), "pointer-events-none", className)}>
      {children}
    </span>
  );
}

export function UploadFileRow({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex w-full min-w-0 items-center gap-3 rounded-lg border bg-card p-3 text-card-foreground",
        className,
      )}
    >
      <div className="relative shrink-0">
        <FileIcon className="size-6 text-muted-foreground" strokeWidth={1.5} />
        <CheckCircle className="absolute -right-1 -bottom-1 size-3 rounded-full bg-background text-green-500" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{PRE_DECK_FILE.name}</span>
          <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
            100%
            <Trash2 className="size-3" />
          </span>
        </div>
        <div className="mt-1 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{PRE_DECK_FILE.size}</span>
          <span className="ml-2 h-1 w-16 overflow-hidden rounded-full bg-muted">
            <span className="block h-full w-full rounded-full bg-green-500" />
          </span>
        </div>
      </div>
    </div>
  );
}

export function UploadDropzone({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-dashed border-neutral-300 bg-background p-6 text-center",
        className,
      )}
    >
      <div className="flex flex-col items-center gap-3">
        <UploadCloud className="size-8 text-muted-foreground" />
        <div className="space-y-1">
          <p className="text-sm font-medium">Replace file</p>
          <p className="text-xs text-muted-foreground">
            Drag & drop or <span className="font-medium text-primary">browse</span>
          </p>
          <p className="text-xs text-muted-foreground">{PRESENTATION_UPLOAD_HINT}</p>
        </div>
      </div>
    </div>
  );
}

export function GenerationProgress({
  title,
  pct,
  phases,
  current,
  sub,
  className,
}: {
  title: string;
  pct: number;
  phases: string[];
  current: number;
  sub?: string;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border border-border bg-neutral-50 p-4", className)}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      <div className="mt-3 h-1 overflow-hidden rounded-full bg-border">
        <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
      </div>
      <ul className="mt-3 space-y-2">
        {phases.map((phase, index) => {
          const state = index < current ? "done" : index === current ? "current" : "pending";
          return (
            <li key={phase} className="flex items-center gap-2.5 text-sm">
              {state === "done" ? (
                <span className="flex size-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                  <Check className="size-2.5" strokeWidth={3} />
                </span>
              ) : state === "current" ? (
                <span className="flex size-4 items-center justify-center rounded-full ring-1 ring-inset ring-primary">
                  <span className="size-1.5 rounded-full bg-primary" />
                </span>
              ) : (
                <span className="size-4 rounded-full border border-dashed border-neutral-300" />
              )}
              <span className={cn(state === "pending" && "text-muted-foreground")}>{phase}</span>
              {state === "current" && sub ? (
                <span className="ml-auto text-xs tabular-nums text-muted-foreground">{sub}</span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function HandyoutGenerationProgress({ className }: { className?: string }) {
  const { current, total, pct } = PRE_GENERATION_PROGRESS;
  return (
    <GenerationProgress
      title="Creating your handyout"
      pct={pct}
      phases={HANDYOUT_PHASES}
      current={1}
      sub={`${current} / ${total}`}
      className={className}
    />
  );
}
